import { Conditional } from "@@/shared/conditional";
import { HorizontalDivider, VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { _FormFieldDescription } from "@@/shared/form/_form-field-description";
import type { FormId } from "@@/shared/form/form-and-field-id";
import { useFormId } from "@@/shared/form/form-id.context";

import { FormErrorMessages } from "@@/shared/form/form2-error-messages";
import { useFormColors } from "@@/shared/form/use-form-colors";
import { Icon } from "@@/shared/icons/icon";
import { paddingToCssValue } from "@@/shared/padding";
import { FieldTitle } from "@@/shared/text/field-title";
import type { AppTheme } from "@@/styles/theme";
import {
    useIsTranslatableNotEmpty,
    useTranslate,
} from "@@/translations/use-translate";
import type { Interpolation } from "@emotion/react";
import { useTheme } from "@emotion/react";
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";
import { faAsterisk } from "@fortawesome/pro-solid-svg-icons";
import type {
    ColorSet,
    DeepKeysOf,
    Padding,
    Translatable,
    TranslatableValueFactoryParams,
} from "@towni/common";
import { asArray, generateId, setWithinRange } from "@towni/common";
import debounce from "lodash.debounce";
import type React from "react";
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import type { infer as InferZodType, ZodSchema } from "zod";
import { Fade } from "../animations/fade";
import { FadeAndHeight } from "../animations/fade-and-height";
import { TextBox } from "../text";
import type { Form3ErrorMessage } from "./form3.error-message";
import { useForm3Field } from "./form3.use-form-field";

type ValueType = string;

type Props<
    FormData,
    FieldId extends DeepKeysOf<FormData>,
    Value extends string = ValueType,
> = {
    readonly className?: string;
    readonly fieldId: FieldId;
    readonly formId?: FormId;
    readonly type?: "text" | "password" | "email" | "url" | "date" | "tel";
    readonly autoComplete?: string;
    /**
     * Decides wether field should be a text area or regular input of type text. \
     * When set to a number > 0; the number will be used as the number of rows visible for the text-area. \
     * Number of rows defaults to `4` if value of property is set to `true`
     * @type {(boolean | number)}
     */
    readonly multiline?: boolean | number;
    /**
     * Optional element to be rendered before the text input field but within it's container
     * @type {React.JSX.Element}
     */
    readonly preElement?: React.JSX.Element;
    /**
     * Optional element to be rendered after the text input field but within it's container
     * @type {React.JSX.Element}
     */
    readonly postElement?: React.JSX.Element;
    readonly description?: Translatable | TranslatableValueFactoryParams;
    readonly placeholder?: Translatable | TranslatableValueFactoryParams;
    readonly hideDescriptionAfterInput?: boolean;
    readonly label?: Translatable | TranslatableValueFactoryParams;
    readonly charFilters?: RegExp | RegExp[];
    readonly labelDescription?: Translatable | TranslatableValueFactoryParams;
    readonly colorSet?: ColorSet;
    readonly dangerColorSet?: ColorSet;
    readonly hideErrors?: boolean;
    readonly disabled?: boolean;
    readonly spin?: boolean;
    /** Visar fältet som required, även om det inte krävs enligt zod schemat. */
    readonly showAsRequired?: boolean;
    readonly containerCss?: Interpolation<AppTheme> | Interpolation<AppTheme>[];
    readonly inputCss?: Interpolation<AppTheme>;
    /**
     * Callback that will be called with the input element when it is rendered.
     */
    readonly inputCallbackRef?: (
        element: HTMLInputElement | HTMLTextAreaElement | null,
    ) => void;
    readonly padding?: Padding;
    /**
     * If set to true, the height of the text area will adjust to the content.
     */
    readonly autoHeight?: boolean;
    /**
     * Runs on key down and can be used to handle key events.
     * The second argument is a function that can be called to cancel the debounce effect of the internal onChange handler.
     * This can be useful if you want to handle the key event without the debounce effect happening after the key event.
     */
    readonly onKeyDown?: (
        event:
            | React.KeyboardEvent<HTMLInputElement>
            | React.KeyboardEvent<HTMLTextAreaElement>,
        clearDebounce: () => void,
    ) => void | undefined;
    /**
     * Runs on key up and can be used to handle key events.
     * The second argument is a function that can be called to cancel the debounce effect of the internal onChange handler.
     * This can be useful if you want to handle the key event without the debounce effect happening after the key event.
     */
    readonly onKeyUp?: (
        event:
            | React.KeyboardEvent<HTMLInputElement>
            | React.KeyboardEvent<HTMLTextAreaElement>,
        clearDebounce: () => void,
    ) => void | undefined;
    /**
     * When value changes, this callback will be called with the new value.
     */
    readonly onChange?: (value: Value | undefined) => void;
    /**
     * Runs on change and can transform the value before it is set and saved in the form state.
     * This can be used to transform the value before it is saved. It's the transformed value
     * that will be passed to the `onChange` callback.
     */
    readonly onChangePreTransform?: (
        value: Value | undefined | null,
    ) => Value | undefined;
};

const createForm3TextInputComponent = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
>() => {
    const Form3TextInput = <
        Value extends string = ValueType,
        FieldId extends DeepKeysOf<FormData> = DeepKeysOf<FormData>,
    >(
        props: Props<FormData, FieldId, Value>,
    ) => {
        const theme = useTheme();
        const translate = useTranslate();
        const [errors, setErrors] = useState<Form3ErrorMessage[]>([]);
        const formIdFromContext = useFormId({ doNotThrow: true });
        const formId = props.formId || formIdFromContext;
        const field = useForm3Field<
            FormSchema,
            InferZodType<FormSchema>,
            FieldId,
            Value
        >({
            fieldId: props.fieldId,
            formId: props.formId,
        });
        const [initialValue] = useState(field.value);
        const hasErrors = errors.length > 0;
        const InputElement = props.multiline ? "textarea" : "input";

        // Colors
        const { borderColor, backgroundColor, textColor, spinnerColor } =
            useFormColors({
                hasErrors,
                colorSet: props.colorSet,
                dangerColorSet: props.dangerColorSet,
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onChangeHandler = useCallback(
            debounce(value => {
                const processedNewValue = props.onChangePreTransform
                    ? props.onChangePreTransform(value)
                    : value;
                currentValueRef.current = processedNewValue;
                field.setValue(processedNewValue);
                field.setDirty(processedNewValue !== initialValue);
                field.setTouched(true);
                props.onChange?.(processedNewValue);
            }, 300),
            [
                field.setValue,
                field.setDirty,
                field.setTouched,
                initialValue,
                props.onChange,
            ],
        );
        const debounceRef = useRef(onChangeHandler);
        debounceRef.current = onChangeHandler;

        const [key, setKey] = useState(() => generateId());
        const currentValueRef = useRef(field.value);
        useEffect(() => {
            // Update the input field when value
            // changes from outside in the form
            if (currentValueRef.current === field.value) return;
            currentValueRef.current = field.value;
            setKey(generateId());
        }, [field.value]);

        const inputElementRef = useRef<
            HTMLInputElement | HTMLTextAreaElement | null
        >(null);
        const [inputElement, _setInputElement] = useState<
            HTMLInputElement | HTMLTextAreaElement | null
        >();
        const setInputElement = useCallback(
            (node: HTMLInputElement | HTMLTextAreaElement | null): void => {
                inputElementRef.current = node || null;
                props?.inputCallbackRef?.(node || null);
                if (props.autoHeight) _setInputElement(node || null);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [props.autoHeight, props.inputCallbackRef],
        );

        const initialHeightRef = useRef<number | null>(null);

        useLayoutEffect(() => {
            if (!props.autoHeight) return;
            if (!inputElement) return;
            // Measure the height of the content
            const currentHeight = inputElement.getBoundingClientRect().height;
            if (initialHeightRef.current === null) {
                initialHeightRef.current = currentHeight;
            }
            inputElement.style.height = `0px`;
            const elementHeight = setWithinRange(inputElement.scrollHeight, {
                min: initialHeightRef.current,
            });
            // Set the height of the element to the measured content height
            inputElement.style.height = `${elementHeight}px`;
        }, [inputElement, inputElement?.scrollHeight, props.autoHeight]);

        const isNotEmpty = useIsTranslatableNotEmpty();
        const hasLabel = isNotEmpty(props.label);
        const _hasPlaceholder = isNotEmpty(props.placeholder);
        const hasLabelDescription = isNotEmpty(props.labelDescription);
        const required = props.showAsRequired || field.isRequired;

        return (
            <FlexColumn
                className={props.className}
                key={`${formId}_${props.fieldId}`}
                tag={`form-field_${props.fieldId}`}
                crossAxis="stretch">
                <Conditional when={hasLabel}>
                    <FlexRow
                        crossAxis="center"
                        mainAxis="space-between"
                        css={{
                            flexShrink: 0,
                        }}>
                        <FieldTitle
                            htmlFor={props.fieldId}
                            padding={{ left: 2 }}
                            text={props.label ?? ""} // already checked with conditional above
                            required={required}
                        />
                        <Conditional when={hasLabelDescription}>
                            <HorizontalDivider XXS />
                            <FieldTitle
                                padding={{ left: 2 }}
                                text={props.labelDescription ?? ""} // already checked with conditional above
                                weight="400"
                                size="S"
                                css={{
                                    opacity: 0.5,
                                }}
                            />
                        </Conditional>
                    </FlexRow>
                    <VerticalDivider XS />
                </Conditional>
                <FlexColumn
                    style={{
                        backgroundColor,
                        borderColor,
                    }}
                    css={[
                        {
                            cursor: "pointer",
                            borderWidth: 1,
                            borderStyle: "solid",
                            borderRadius: theme.radius,
                            flex: 1,
                            opacity: props.disabled ? 0.5 : 1,
                            padding: paddingToCssValue(
                                props.padding ?? { all: 14, topBottom: 9 },
                            ),
                            transition:
                                "background-color 0.3s ease-in-out, color 0.3s ease-in-out, border-color 0.3s ease-in-out",
                        },
                        ...(props.containerCss
                            ? asArray(props.containerCss)
                            : []),
                    ]}
                    crossAxis="stretch"
                    mainAxis="flex-start"
                    onClick={() => {
                        inputElementRef.current?.focus();
                    }}>
                    <div css={{ position: "relative" }}>
                        <div css={{ marginTop: -4 }}>
                            <FadeAndHeight
                                options={{ speed: "default" }}
                                show={
                                    !hasLabel &&
                                    !!field.value &&
                                    !!props.placeholder
                                }>
                                <TextBox
                                    text={props.placeholder}
                                    size={0.825}
                                    color={theme.colors.black.light60}
                                />
                                <VerticalDivider XXS />
                            </FadeAndHeight>
                        </div>
                    </div>
                    <FlexRow
                        css={{ flex: 1 }}
                        crossAxis="center"
                        mainAxis="space-between">
                        <Conditional when={!!props.preElement}>
                            <FlexRow
                                css={{
                                    flexShrink: 0,
                                    flexGrow: 0,
                                }}>
                                {props.preElement ?? null}
                            </FlexRow>
                            <HorizontalDivider />
                        </Conditional>
                        <InputElement
                            key={key}
                            ref={setInputElement}
                            placeholder={translate(props.placeholder)}
                            onBeforeInput={
                                // If charFilters is set,
                                // prevent input of characters that match the filter(s)
                                props.charFilters
                                    ? event => {
                                          const input = (
                                              event as unknown as InputEvent
                                          ).data as string;
                                          const prevent = asArray(
                                              props.charFilters,
                                          ).some(filter => {
                                              filter.lastIndex = 0; // because of global flag weirdness
                                              return filter.test(input);
                                          });
                                          if (prevent) {
                                              event.preventDefault();
                                              return false;
                                          }
                                          return true;
                                      }
                                    : undefined
                            }
                            type={props.type ?? "text"}
                            rows={
                                !props.multiline
                                    ? undefined
                                    : typeof props.multiline === "number"
                                      ? props.multiline
                                      : 4
                            }
                            autoComplete={props.autoComplete}
                            defaultValue={field.value || undefined}
                            onBlur={() => {
                                field.setTouched(true);
                                setErrors(field.validate());
                            }}
                            onKeyDown={
                                props.onKeyDown
                                    ? e => {
                                          if (!props.onKeyDown) return;
                                          props.onKeyDown(e, () =>
                                              debounceRef.current.cancel(),
                                          );
                                      }
                                    : undefined
                            }
                            onKeyUp={
                                props.onKeyUp
                                    ? e => {
                                          if (!props.onKeyUp) return;
                                          props.onKeyUp(e, () =>
                                              debounceRef.current.cancel(),
                                          );
                                      }
                                    : undefined
                            }
                            disabled={field.form.mutation?.isPending}
                            onChange={e => {
                                const newValue = e.target.value.trim();
                                debounceRef.current(newValue);
                            }}
                            style={{
                                backgroundColor,
                                color: textColor,
                            }}
                            css={[
                                {
                                    userSelect: "auto",
                                    border: "none",
                                    flex: 1,
                                    fontSize: "1rem",
                                    minHeight: 30,
                                    transition:
                                        "background-color 0.3s ease-in-out, color 0.3s ease-in-out",
                                },
                                props.inputCss,
                                hasErrors && {
                                    borderColor: theme.colors.danger.asString,
                                },
                            ]}
                        />
                        <Conditional when={!!props.postElement}>
                            <HorizontalDivider />
                            <FlexRow
                                css={{
                                    flexGrow: 0,
                                    flexShrink: 0,
                                }}>
                                {props.postElement ?? null}
                            </FlexRow>
                        </Conditional>
                        <Fade show={required && !field.value} duration={0.2}>
                            <FlexRow crossAxis="center">
                                <HorizontalDivider />
                                <Icon
                                    icon={faAsterisk}
                                    color={theme.colors.danger}
                                    size={0.5}
                                />
                            </FlexRow>
                        </Fade>
                        <Conditional when={!!props.spin}>
                            <FlexRow
                                css={{
                                    flexGrow: 0,
                                    flexShrink: 0,
                                }}>
                                <HorizontalDivider />
                                <Icon
                                    icon={faSpinnerThird}
                                    spin
                                    color={spinnerColor}
                                />
                            </FlexRow>
                        </Conditional>
                    </FlexRow>
                </FlexColumn>
                <_FormFieldDescription
                    hasErrors={hasErrors}
                    isDirty={field.dirty}
                    description={props.description}
                    hideDescriptionAfterInput={
                        !!props.hideDescriptionAfterInput
                    }
                    css={{
                        marginTop: 5,
                    }}
                />
                <Conditional when={!props.hideErrors}>
                    <FormErrorMessages errors={errors} />
                </Conditional>
            </FlexColumn>
        );
    };

    return Form3TextInput;
};
export { createForm3TextInputComponent };
export type {
    ValueType as Form3TextInputPossibleValueTypes,
    Props as Form3TextInputProps,
};
